import React from 'react';

import { AlexGoLanguage, LOCALE_BY_LANGUAGE, useTextContext } from 'Contexts/textContext';
import { Dropdown } from 'DesignLibrary/molecules/Dropdown';
import { sendInsightsEvent } from 'Utils/insights';

interface LanguageOption {
  display: string;
  description: string;
  value: string;
}

// Labels for each ALEX Go language, in the language itself. The locale comes from
// LOCALE_BY_LANGUAGE so the selector and the content fetch can't drift apart.
const LANGUAGE_OPTIONS: Record<AlexGoLanguage, LanguageOption> = {
  english: { display: 'EN', description: 'English', value: LOCALE_BY_LANGUAGE.english },
  spanish: { display: 'ES', description: 'Español', value: LOCALE_BY_LANGUAGE.spanish },
  chinese: { display: 'ZH', description: '中文', value: LOCALE_BY_LANGUAGE.chinese },
  haitian_creole: {
    display: 'HT',
    description: 'Kreyòl ayisyen',
    value: LOCALE_BY_LANGUAGE.haitian_creole,
  },
};

// The languages this build can actually offer, in the customer's configured order.
// `enabledLanguages` is runtime data from bootstrap, so it can name a language this
// build doesn't know how to render yet (platform-api adds one before the frontend
// catches up). Those are left out rather than mapped to `undefined` — otherwise the
// whole navigation crashes on an unknown value.
//
// Exported so the nav can decide whether to render the toggle's wrapper at all,
// without duplicating this filter.
export const useLanguageOptions = (): LanguageOption[] => {
  const { enabledLanguages } = useTextContext();

  return enabledLanguages
    .filter((language) => language in LANGUAGE_OPTIONS)
    .map((language) => LANGUAGE_OPTIONS[language]);
};

// Whether the language selector should render at all: only when the customer enabled
// more than one language this build can offer.
export const useShowLanguageToggle = (): boolean => useLanguageOptions().length > 1;

const LanguageToggle = () => {
  const { setLocale, locale, retrieveContentfulData } = useTextContext();
  const options = useLanguageOptions();

  const handleLocaleSwitch = (e) => {
    const value = e.target.value;
    if (value !== 'default') {
      sendInsightsEvent(e, 'update_language', {
        locale: value,
      });
      setLocale(value);
    }
  };

  // Display only: for the render or two before TextProvider reconciles a locale the
  // customer doesn't enable, show the first offered language rather than leaving the
  // dropdown on a value that isn't in its own option list. Correcting the locale is
  // TextProvider's job — it stays mounted on every route, this component does not.
  const validValues = options.map((o) => o.value);
  const selectedValue = validValues.includes(locale) ? locale : (options[0]?.value ?? 'en-US');

  // Don't render a selector when the customer enabled a single language.
  if (options.length <= 1) return null;

  return (
    <Dropdown
      label={retrieveContentfulData<string>('top_level_navigation.language_toggle_label')}
      keyHint="language"
      options={options}
      selectedValue={selectedValue}
      groupName="language"
      isOpen={false}
      handleChange={handleLocaleSwitch}
      buttonProps={{
        size: 'small',
        iconLeft: 'Globe',
        iconProps: {
          color: '--primary-blue',
          size: 20,
        },
        isDisabled: false,
      }}
    />
  );
};

export default LanguageToggle;
